import { useMemo, useState } from "react";

/**
 * Section mockup — Signature interactive Evidence Map.
 *
 * A single, connected instrument (not a chart, not a table):
 *   – A central recommendation node.
 *   – 3–5 material claims connected to it, with line weight and colour
 *     communicating the nature of the underlying evidence.
 *   – Each claim connects visibly to its source document(s).
 *   – Unresolved questions extend as distinct open branches (dashed, no
 *     terminating document).
 *   – Mandate alignment sits below the map as a separate analytical layer.
 *   – Focusing a claim illuminates only its supporting relationships; the
 *     rest of the map gently recedes.
 */

const CENTER = { x: 500, y: 340 };

// Claim positions on the map (radial around the center).
const CLAIMS = [
  {
    id: "c1",
    label: "Anchor off-taker contracts secure 74% of Y1 revenue",
    short: "Anchor coverage",
    tone: "verified",
    x: 500, y: 108,
    materiality: "High",
    sources: [
      {
        name: "Independent Engineering Report — RAM Advisory",
        page: "p. 34, §4.2",
        excerpt:
          "12-year PPA obligations across Kenya and Tanzania sites total USD 42.6M through 2033, with anchor coverage at 74% of Y1 revenue.",
        status: "Verified",
        x: 500, y: 22,
      },
    ],
    openQuestion: null,
  },
  {
    id: "c2",
    label: "Deployment reduced from 18 to 5 months per site (v2)",
    short: "Deployment cadence",
    tone: "supported",
    x: 858, y: 320,
    materiality: "Medium",
    sources: [
      {
        name: "Confidential Investment Memorandum",
        page: "p. 19, Fig. 3",
        excerpt:
          "Second-generation containerised design shortened site commissioning from 18 to 5 months across the nine v2 deployments to date.",
        status: "Supported",
        x: 964, y: 250,
      },
    ],
    openQuestion: null,
  },
  {
    id: "c3",
    label: "Top-3 tenants concentrate 41% of Y3 revenue",
    short: "Concentration",
    tone: "review",
    x: 754, y: 574,
    materiality: "High",
    sources: [
      {
        name: "Financial model — Solaris Mesh v3.9",
        page: "sheet «Concentration», row 42",
        excerpt:
          "Cumulative revenue share attributable to the three largest anchor tenants reaches 41% by Year 3 under the base case.",
        status: "Requires review",
        x: 878, y: 636,
      },
    ],
    openQuestion: "Downside IRR under 12% attrition scenario?",
  },
  {
    id: "c4",
    label: "Sovereign tariff visibility 24 months across 3 markets",
    short: "Sovereign policy",
    tone: "review",
    x: 246, y: 574,
    materiality: "Medium",
    sources: [
      {
        name: "Diligence memo — Q4 policy review",
        page: "§3.2",
        excerpt:
          "Feed-in tariff regime under active revision in two of the three operating jurisdictions; 24-month visibility on published rates.",
        status: "Requires review",
        x: 122, y: 636,
      },
    ],
    openQuestion: "Currency structuring of anchor contracts?",
  },
  {
    id: "c5",
    label: "Management: v2 pipeline ≥ 60 sites in 24 months",
    short: "Pipeline assertion",
    tone: "assertion",
    x: 142, y: 320,
    materiality: "Medium",
    sources: [
      {
        name: "Management memo — Feb 2026",
        page: "p. 4",
        excerpt:
          "Management letter attests to a signed development pipeline of ≥ 60 sites over the next 24 months; independent verification pending.",
        status: "Management assertion",
        x: 36, y: 250,
      },
    ],
    openQuestion: null,
  },
];

const MANDATE = [
  { key: "financial",      label: "Financial",       tone: "gold" },
  { key: "strategic",      label: "Strategic",       tone: "forest" },
  { key: "governance",     label: "Governance",      tone: "review" },
  { key: "sustainability", label: "Sustainability",  tone: "gold" },
  { key: "family",         label: "Family priority", tone: "forest" },
];

const LEGEND = [
  { tone: "verified",  label: "Independently supported" },
  { tone: "supported", label: "Supplied evidence" },
  { tone: "assertion", label: "Management assertion" },
  { tone: "review",    label: "Requires review" },
];

export default function DecisionRecordMockup() {
  const [focused, setFocused] = useState(null);
  const focusClaim = useMemo(() => CLAIMS.find((c) => c.id === focused) || null, [focused]);
  const isRecessed = (id) => focused && focused !== id;

  return (
    <div
      data-testid="decision-record-evidence-map"
      data-focus={focused ? "on" : "off"}
      className="iv-canvas w-full rounded-[3px]"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-8 pt-6 pb-5 border-b border-[var(--ivc-line)]">
        <div className="flex items-center gap-3">
          <span className="iv-ev-dot iv-ev-dot--verified" />
          <span className="font-mono text-[11px] tracking-[0.30em] uppercase text-[var(--ivc-gold-2)]">
            Evidence Map
          </span>
        </div>
        <div className="hidden md:flex items-center gap-5">
          {LEGEND.map((l) => (
            <span key={l.tone} className="inline-flex items-center gap-1.5 font-mono text-[10.5px] tracking-[0.14em] uppercase text-[var(--ivc-ivory-3)]">
              <span className={`iv-ev-dot iv-ev-dot--${l.tone}`} />
              {l.label}
            </span>
          ))}
        </div>
      </div>

      {/* Map + focus panel */}
      <div className="grid grid-cols-12">
        {/* SVG Map */}
        <div className="col-span-12 lg:col-span-8 border-b lg:border-b-0 lg:border-r border-[var(--ivc-line-strong)] p-4 md:p-6">
          <div className="relative">
            <svg
              viewBox="0 0 1000 680"
              className="w-full h-auto"
              role="img"
              aria-label="Interactive Evidence Map"
              onMouseLeave={() => setFocused(null)}
            >
              <defs>
                <radialGradient id="em-glow" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="rgba(212,183,116,0.28)" />
                  <stop offset="70%" stopColor="rgba(212,183,116,0)" />
                </radialGradient>
                <linearGradient id="em-node-fill" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor="rgba(27,31,29,0.9)" />
                  <stop offset="100%" stopColor="rgba(15,18,17,0.95)" />
                </linearGradient>
              </defs>

              {/* ambient glow behind the center */}
              <circle cx={CENTER.x} cy={CENTER.y} r="230" fill="url(#em-glow)" />

              {/* connectors: claim → center */}
              {CLAIMS.map((c) => (
                <path
                  key={`link-${c.id}`}
                  d={curve(c.x, c.y, CENTER.x, CENTER.y)}
                  className={`iv-em-conn iv-em-conn--${c.tone} ${focused === c.id ? "iv-em-conn--focused" : ""}`}
                  strokeOpacity={focused && focused !== c.id ? 0.12 : undefined}
                />
              ))}

              {/* connectors: source → claim */}
              {CLAIMS.flatMap((c) =>
                c.sources.map((s, i) => (
                  <path
                    key={`src-link-${c.id}-${i}`}
                    d={curve(s.x, s.y, c.x, c.y)}
                    className={`iv-em-conn iv-em-conn--${c.tone} ${focused === c.id ? "iv-em-conn--focused" : ""}`}
                    strokeOpacity={focused && focused !== c.id ? 0.10 : undefined}
                  />
                ))
              )}

              {/* open-question branches (dashed, no destination) */}
              {CLAIMS.filter((c) => c.openQuestion).map((c) => {
                const [ox, oy] = openQuestionEndpoint(c);
                return (
                  <g key={`oq-${c.id}`} opacity={focused && focused !== c.id ? 0.18 : 1}>
                    <path
                      d={curve(c.x, c.y, ox, oy)}
                      className="iv-em-conn iv-em-conn--review"
                    />
                    <circle cx={ox} cy={oy} r="12" fill="rgba(224,169,79,0.16)" stroke="rgba(224,169,79,0.5)" />
                    <text x={ox} y={oy + 4} textAnchor="middle" fill="var(--ivc-amber)" style={{ fontSize: 13, fontFamily: "'Cormorant Garamond',serif", fontStyle: "italic" }}>?</text>
                  </g>
                );
              })}

              {/* Source document nodes */}
              {CLAIMS.flatMap((c) =>
                c.sources.map((s, i) => (
                  <SourceNode
                    key={`src-${c.id}-${i}`}
                    x={s.x}
                    y={s.y}
                    tone={c.tone}
                    recessed={isRecessed(c.id)}
                    label={s.page}
                  />
                ))
              )}

              {/* Central recommendation node */}
              <g>
                <rect
                  x={CENTER.x - 155}
                  y={CENTER.y - 58}
                  width="310"
                  height="116"
                  rx="4"
                  fill="url(#em-node-fill)"
                  stroke="rgba(212,183,116,0.5)"
                />
                <text x={CENTER.x} y={CENTER.y - 24} textAnchor="middle" fill="var(--ivc-gold-2)"
                      style={{ fontSize: 11, letterSpacing: "0.3em", textTransform: "uppercase", fontFamily: "'IBM Plex Mono',monospace" }}>
                  Recommendation
                </text>
                <text x={CENTER.x} y={CENTER.y + 6} textAnchor="middle" fill="var(--ivc-ivory)"
                      style={{ fontSize: 20, fontFamily: "'Cormorant Garamond',serif" }}>
                  Conditional approval
                </text>
                <text x={CENTER.x} y={CENTER.y + 32} textAnchor="middle" fill="var(--ivc-ivory-3)"
                      style={{ fontSize: 12, fontFamily: "'Manrope',sans-serif" }}>
                  Pending independent counterfactual
                </text>
              </g>

              {/* Claim nodes */}
              {CLAIMS.map((c) => (
                <ClaimNode
                  key={c.id}
                  claim={c}
                  focused={focused === c.id}
                  recessed={isRecessed(c.id)}
                  onFocus={() => setFocused(c.id)}
                  onBlur={() => setFocused(null)}
                />
              ))}
            </svg>
          </div>
        </div>

        {/* Focus / helper panel */}
        <aside className="col-span-12 lg:col-span-4 p-6 md:p-8 flex flex-col" data-testid="evidence-focus-panel">
          <div className="text-[11px] font-mono tracking-[0.24em] uppercase text-[var(--ivc-ivory-3)]">
            {focusClaim ? "Focused claim" : "Interactive map"}
          </div>

          {focusClaim ? (
            <>
              <p className="mt-3 font-serif-display text-[22px] leading-[1.15] text-[var(--ivc-ivory)]">
                {focusClaim.label}
              </p>

              <div className="mt-5 flex items-center gap-2">
                <span className={`iv-ev-dot iv-ev-dot--${focusClaim.tone}`} />
                <span className="font-mono text-[10.5px] tracking-[0.16em] uppercase text-[var(--ivc-ivory-2)]">
                  {toneLabel(focusClaim.tone)}
                </span>
                <span className="text-[var(--ivc-ivory-4)] mx-1">·</span>
                <span className="font-mono text-[10.5px] tracking-[0.16em] uppercase text-[var(--ivc-ivory-3)]">
                  Materiality · {focusClaim.materiality}
                </span>
              </div>

              <div className="mt-6 space-y-4 flex-1">
                {focusClaim.sources.map((s) => (
                  <div key={s.name} className="border-l border-[var(--ivc-line-warm)] pl-4">
                    <div className="font-mono text-[11px] tracking-[0.04em] text-[var(--ivc-ivory-2)]">
                      {s.name}
                    </div>
                    <div className="font-mono text-[10.5px] tracking-[0.10em] text-[var(--ivc-ivory-3)] mt-0.5">
                      {s.page}
                    </div>
                    <p className="font-serif-display italic text-[15px] leading-relaxed text-[var(--ivc-ivory)] mt-3">
                      &ldquo;{s.excerpt}&rdquo;
                    </p>
                    <div className="mt-3 font-mono text-[10px] tracking-[0.20em] uppercase text-[var(--ivc-gold-2)]">
                      {s.status}
                    </div>
                  </div>
                ))}
                {focusClaim.openQuestion && (
                  <div className="border-l border-[rgba(224,169,79,0.45)] pl-4">
                    <div className="font-mono text-[10px] tracking-[0.20em] uppercase text-[var(--ivc-amber)]">
                      Open question
                    </div>
                    <p className="font-serif-display italic text-[14.5px] leading-relaxed text-[var(--ivc-ivory-2)] mt-2">
                      {focusClaim.openQuestion}
                    </p>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <p className="mt-3 font-serif-display text-[19px] leading-[1.25] text-[var(--ivc-ivory-2)]">
                Focus a claim to reveal the source document, the page reference,
                the relevant excerpt and its current review status.
              </p>
              <ul className="mt-6 space-y-3 flex-1">
                {CLAIMS.map((c) => (
                  <li
                    key={c.id}
                    onMouseEnter={() => setFocused(c.id)}
                    className="flex items-start gap-3 cursor-default"
                  >
                    <span className={`iv-ev-dot iv-ev-dot--${c.tone} mt-1.5`} />
                    <span className="text-[13.5px] leading-snug text-[var(--ivc-ivory-2)]">{c.short}</span>
                  </li>
                ))}
              </ul>
            </>
          )}

          <div className="mt-6 pt-4 border-t border-[var(--ivc-line)] flex items-center justify-between">
            <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[var(--ivc-ivory-3)]">
              Record status
            </span>
            <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[var(--ivc-gold-2)]">
              Ready for committee review
            </span>
          </div>
        </aside>
      </div>

      {/* Mandate alignment — separate analytical layer */}
      <div className="border-t border-[var(--ivc-line-strong)] px-6 md:px-10 py-6" data-testid="evidence-mandate-alignment">
        <div className="flex items-center justify-between mb-5">
          <div className="text-[11px] font-mono tracking-[0.24em] uppercase text-[var(--ivc-ivory-3)]">
            Mandate alignment · FO-04 · v3
          </div>
          <div className="font-mono text-[10.5px] tracking-[0.20em] uppercase text-[var(--ivc-gold-2)]">
            Separate analytical layer
          </div>
        </div>
        <ul className="grid grid-cols-1 md:grid-cols-5 gap-4 md:gap-8">
          {MANDATE.map((m) => (
            <li key={m.key} className="flex flex-col gap-2">
              <span
                aria-hidden
                className="h-[3px] w-full"
                style={{
                  background: m.tone === "gold" ? "var(--ivc-gold)" :
                              m.tone === "forest" ? "var(--ivc-forest)" :
                              "var(--ivc-amber)",
                  opacity: m.tone === "review" ? 0.7 : 0.9,
                }}
              />
              <span className="font-mono text-[10.5px] tracking-[0.18em] uppercase text-[var(--ivc-ivory-2)]">
                {m.label}
              </span>
              <span className="font-mono text-[10px] tracking-[0.14em] uppercase text-[var(--ivc-ivory-3)]">
                {m.tone === "gold" ? "Strong" : m.tone === "forest" ? "Aligned" : "Review"}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/* ------------------------------------------------------- helpers ------- */

function toneLabel(t) {
  return {
    verified: "Independently supported",
    supported: "Supplied evidence",
    assertion: "Management assertion",
    review: "Requires review",
  }[t];
}

// Bezier curve between two points — subtle arc for organic connectivity.
function curve(x1, y1, x2, y2) {
  const mx = (x1 + x2) / 2;
  const my = (y1 + y2) / 2;
  // Perpendicular offset for a gentle arc.
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.max(1, Math.hypot(dx, dy));
  const ox = (-dy / len) * 22;
  const oy = (dx / len) * 22;
  return `M ${x1} ${y1} Q ${mx + ox} ${my + oy} ${x2} ${y2}`;
}

// End of an open-question branch — offset outward from the claim.
function openQuestionEndpoint(c) {
  const dx = c.x - CENTER.x;
  const dy = c.y - CENTER.y;
  const len = Math.max(1, Math.hypot(dx, dy));
  return [c.x + (dx / len) * 90, c.y + (dy / len) * 90];
}

/* ------------------------------------------------- SVG sub-components -- */

function ClaimNode({ claim, focused, recessed, onFocus, onBlur }) {
  const strokeMap = {
    verified: "rgba(212,183,116,0.85)",
    supported: "rgba(107,178,140,0.75)",
    assertion: "rgba(198,192,174,0.4)",
    review: "rgba(224,169,79,0.7)",
  };
  const opacity = recessed ? 0.28 : 1;
  const w = 210;
  const h = 62;
  return (
    <g
      className="iv-em-node"
      onMouseEnter={onFocus}
      onFocus={onFocus}
      onBlur={onBlur}
      tabIndex={0}
      style={{ opacity, cursor: "default" }}
    >
      <rect
        x={claim.x - w / 2}
        y={claim.y - h / 2}
        width={w}
        height={h}
        rx="3"
        fill="rgba(15,18,17,0.92)"
        stroke={strokeMap[claim.tone]}
        strokeWidth={focused ? 1.6 : 1}
      />
      <circle
        cx={claim.x - w / 2 + 14}
        cy={claim.y}
        r="5"
        fill={
          claim.tone === "verified" ? "var(--ivc-gold)" :
          claim.tone === "supported" ? "var(--ivc-forest)" :
          claim.tone === "review" ? "var(--ivc-amber)" : "var(--ivc-ivory-3)"
        }
      />
      <text
        x={claim.x - w / 2 + 28}
        y={claim.y + 4}
        fill="var(--ivc-ivory)"
        style={{ fontSize: 13, fontFamily: "'Manrope',sans-serif" }}
      >
        <tspan>{claim.short}</tspan>
      </text>
      <text
        x={claim.x - w / 2 + 28}
        y={claim.y + 20}
        fill="var(--ivc-ivory-3)"
        style={{ fontSize: 10, letterSpacing: "0.10em", textTransform: "uppercase", fontFamily: "'IBM Plex Mono',monospace" }}
      >
        {claim.materiality} materiality
      </text>
    </g>
  );
}

function SourceNode({ x, y, tone, recessed, label }) {
  const stroke = {
    verified: "rgba(212,183,116,0.65)",
    supported: "rgba(107,178,140,0.55)",
    assertion: "rgba(198,192,174,0.32)",
    review: "rgba(224,169,79,0.55)",
  }[tone];
  return (
    <g className="iv-em-node" style={{ opacity: recessed ? 0.22 : 1 }}>
      <rect x={x - 40} y={y - 14} width="80" height="28" rx="2"
            fill="rgba(20,24,23,0.9)" stroke={stroke} strokeWidth="0.8" />
      <text x={x} y={y + 4} textAnchor="middle" fill="var(--ivc-ivory-3)"
            style={{ fontSize: 10, letterSpacing: "0.12em", fontFamily: "'IBM Plex Mono',monospace" }}>
        {label}
      </text>
    </g>
  );
}
